// ---------------------------------------------------------------------------
// AddressStep — Tabbed address / coordinates entry
// ---------------------------------------------------------------------------
// Two tabs:
//   1. **Address** — text inputs for address1, city, postal_code
//   2. **Coordinates** — latitude / longitude inputs (bypass geocoding)
//
// The backend auto-geocodes when the address tab is used. The coordinates
// tab lets users supply lat/lon directly when their address is
// non-standard or geocoding has previously failed.
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { Input } from '@/components/ui/Input';
import { t } from '@/i18n';
import { useCollectedData } from '@/hooks/useCollectedData';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import type { StepProps } from '@/types/steps';
import { getStepIcon } from '@/utils/stepIcons';
import styles from './AddressStep.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = 'address' | 'coordinates';

interface AddressForm {
  address1: string;
  city: string;
  postal_code: string;
}

interface CoordsForm {
  latitude: string;
  longitude: string;
}

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

const isValidLat = (v: string): boolean => {
  const n = parseFloat(v);
  return !isNaN(n) && n >= -90 && n <= 90;
};

const isValidLon = (v: string): boolean => {
  const n = parseFloat(v);
  return !isNaN(n) && n >= -180 && n <= 180;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the `display` string from parts, filtering out empty values. */
function buildDisplay(form: AddressForm): string {
  const parts = [form.address1, form.postal_code, form.city].filter(Boolean);
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// AddressStep
// ---------------------------------------------------------------------------

export const AddressStep = memo(function AddressStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();
  const { demoMode } = useWidgetConfig();

  // ── Determine initial tab from collected_data ──
  // Coordinates tab is only available in demo mode.
  // If user previously submitted via coordinates tab, start on that tab.
  const initialTab = useMemo<Tab>(() => {
    if (!demoMode) return 'address';
    const coords = collectedData['coordinates'];
    const addr = collectedData['address'];
    // If we have coordinates but no address, user used coords tab before
    if (coords && typeof coords === 'object' && !addr) return 'coordinates';
    return 'address';
  }, [collectedData, demoMode]);

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  // ── Address tab state ──
  const initialAddr = useMemo<AddressForm>(() => {
    const addr = collectedData['address'];
    if (typeof addr === 'object' && addr !== null) {
      const a = addr as Record<string, unknown>;
      const a1 = typeof a['address1'] === 'string' ? a['address1'] : '';
      const ct = typeof a['city'] === 'string' ? a['city'] : '';
      const pc = typeof a['postal_code'] === 'string' ? a['postal_code'] : '';
      if (a1) return { address1: a1, city: ct, postal_code: pc };
    }
    const rc = options.rule_context;
    return {
      address1: typeof rc['address1'] === 'string' ? rc['address1'] : '',
      city: typeof rc['city'] === 'string' ? rc['city'] : '',
      postal_code: typeof rc['postal_code'] === 'string' ? rc['postal_code'] : '',
    };
  }, [collectedData, options.rule_context]);

  const [addrForm, setAddrForm] = useState<AddressForm>(initialAddr);

  // ── Coordinates tab state ──
  const initialCoords = useMemo<CoordsForm>(() => {
    const coords = collectedData['coordinates'];
    if (typeof coords === 'object' && coords !== null) {
      const c = coords as Record<string, unknown>;
      const lat = typeof c['latitude'] === 'number' ? String(c['latitude']) : '';
      const lon = typeof c['longitude'] === 'number' ? String(c['longitude']) : '';
      if (lat && lon) return { latitude: lat, longitude: lon };
    }
    return { latitude: '', longitude: '' };
  }, [collectedData]);

  const [coordsForm, setCoordsForm] = useState<CoordsForm>(initialCoords);

  // ── Validation ──
  const isAddrValid = useMemo(() => addrForm.address1.trim().length > 0, [addrForm.address1]);

  const isCoordsValid = useMemo(
    () => isValidLat(coordsForm.latitude) && isValidLon(coordsForm.longitude),
    [coordsForm.latitude, coordsForm.longitude],
  );

  const isValid = activeTab === 'address' ? isAddrValid : isCoordsValid;

  // ── Handlers ──
  const handleAddrChange = useCallback(
    (field: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddrForm((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const handleCoordsChange = useCallback(
    (field: keyof CoordsForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCoordsForm((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (!isValid) return;

    if (activeTab === 'address') {
      const display = buildDisplay(addrForm);
      onSubmit({
        display,
        address1: addrForm.address1.trim(),
        city: addrForm.city.trim() || undefined,
        postal_code: addrForm.postal_code.trim() || undefined,
      });
    } else {
      onSubmit({
        coordinates: {
          latitude: parseFloat(coordsForm.latitude),
          longitude: parseFloat(coordsForm.longitude),
        },
      });
    }
  }, [isValid, activeTab, addrForm, coordsForm, onSubmit]);

  const handleTabKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveTab((prev) => (prev === 'address' ? 'coordinates' : 'address'));
      }
    },
    [],
  );

  return (
    <>
      <StepHeading title={t('steps.address.title')} subtitle={t('steps.address.subtitle')} icon={getStepIcon(stepId)} />

      {/* Tab bar — only shown in demo mode when coordinates tab is available */}
      {demoMode && (
        <div className={styles['tabs']} role="tablist" aria-label={t('steps.address.title')}>
          <button
            type="button"
            role="tab"
            id="tab-address"
            aria-selected={activeTab === 'address'}
            aria-controls="panel-address"
            className={styles['tab']}
            onClick={() => setActiveTab('address')}
            onKeyDown={handleTabKey}
            tabIndex={activeTab === 'address' ? 0 : -1}
          >
            {t('steps.address.addressTab')}
          </button>
          <button
            type="button"
            role="tab"
            id="tab-coordinates"
            aria-selected={activeTab === 'coordinates'}
            aria-controls="panel-coordinates"
            className={styles['tab']}
            onClick={() => setActiveTab('coordinates')}
            onKeyDown={handleTabKey}
            tabIndex={activeTab === 'coordinates' ? 0 : -1}
          >
            {t('steps.address.coordsTab')}
          </button>
        </div>
      )}

      {/* Address tab panel */}
      {activeTab === 'address' && (
        <div
          role="tabpanel"
          id="panel-address"
          aria-labelledby="tab-address"
          className={styles['form']}
        >
          <div className={styles['fields']}>
            <Input
              label={t('steps.address.addressLabel')}
              name="address1"
              value={addrForm.address1}
              onChange={handleAddrChange('address1')}
              placeholder={t('steps.address.addressPlaceholder')}
              required
            />
            <div className={styles['row']}>
              <Input
                label={t('steps.address.postalCodeLabel')}
                name="postal_code"
                value={addrForm.postal_code}
                onChange={handleAddrChange('postal_code')}
              />
              <Input
                label={t('steps.address.cityLabel')}
                name="city"
                value={addrForm.city}
                onChange={handleAddrChange('city')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Coordinates tab panel */}
      {activeTab === 'coordinates' && (
        <div
          role="tabpanel"
          id="panel-coordinates"
          aria-labelledby="tab-coordinates"
          className={styles['form']}
        >
          <p className={styles['coords-hint']}>{t('steps.address.coordsHint')}</p>
          <div className={styles['fields']}>
            <Input
              label={t('steps.address_validation.latitudeLabel')}
              name="latitude"
              type="number"
              value={coordsForm.latitude}
              onChange={handleCoordsChange('latitude')}
              error={
                coordsForm.latitude && !isValidLat(coordsForm.latitude)
                  ? t('steps.address.latRange')
                  : undefined
              }
              required
            />
            <Input
              label={t('steps.address_validation.longitudeLabel')}
              name="longitude"
              type="number"
              value={coordsForm.longitude}
              onChange={handleCoordsChange('longitude')}
              error={
                coordsForm.longitude && !isValidLon(coordsForm.longitude)
                  ? t('steps.address.lonRange')
                  : undefined
              }
              required
            />
          </div>
        </div>
      )}

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
        disabled={!isValid}
      />
    </>
  );
});
